import { useState, useEffect, useRef } from "react";
import CollapsibleSection from "@components/common/Section/CollapsibleSection";
import Button from "@components/common/Button";
import Checkbox from "@components/common/Checkbox";
import {
	organisation as organisation_permissions,
	PermissionSet,
	sets as permissionSets,
} from "@components/Auth/permissions";
import UserPermissionSelect from "./UserPermissionSelect.jsx";
import { useUserPermissions } from "./UserPermissionsContext.jsx";
import Badge from "@components/common/Badge";

function mapPermissionsFromUser(userPermissions = []) {
	return organisation_permissions.map((group) => ({
		...group,
		items: group.items.map((item) => ({
			...item,
			checked:
				!!userPermissions.find(
					(permission) => permission.type === item.key
				) &&
				(!item.needs ||
					item.needs.every((need) =>
						userPermissions.find(
							(permission) => permission.type === need
						)
					)),
		})),
	}));
}

function mapPermissionsFromPreset(selectedSet) {
	const preset = permissionSets[selectedSet]?.preset || [];

	return organisation_permissions.map((group) => ({
		...group,
		items: group.items.map((item) => ({
			...item,
			checked: preset.includes(item.key),
		})),
	}));
}

function hasCheckedPermission(groups, permissionKey) {
	return groups.some((group) =>
		group.items.some((item) => item.key === permissionKey && item.checked)
	);
}

function normalizePermissions(groups) {
	return groups.map((group) => ({
		...group,
		items: group.items.map((item) => {
			const needsMet =
				!item.needs ||
				item.needs.every((need) => hasCheckedPermission(groups, need));

			if (item.checked && !needsMet) {
				return { ...item, checked: false };
			}

			return item;
		}),
	}));
}

function UserOrganisationPermissions({
	userId,
	organisationId,
	organisationName,
	userPermissions,
	permissionSet,
	onDelete,
	onSaveSuccess,
	disabled,
	isNewOrganisation: _isNewOrganisation = false,
}) {
	const [permissions, setPermissions] = useState([]);
	const [selectedSet, setSelectedSet] = useState(
		permissionSet || PermissionSet.MEMBER_WAR
	);

	const {
		setSelectedPermissionSet,
		user,
		setOrganisationPermissions,
		saveAllPermissions,
	} = useUserPermissions();
	const contentRef = useRef(null);

	useEffect(() => {
		setSelectedSet(permissionSet || PermissionSet.MEMBER_WAR);
	}, [permissionSet]);

	useEffect(() => {
		if (_isNewOrganisation) {
			return;
		}
		setPermissions(
			normalizePermissions(mapPermissionsFromUser(userPermissions))
		);
	}, [_isNewOrganisation, userPermissions]);

	useEffect(() => {
		if (!_isNewOrganisation) {
			return;
		}
		setPermissions(
			normalizePermissions(mapPermissionsFromPreset(selectedSet))
		);
	}, [_isNewOrganisation, selectedSet]);

	// Update context whenever permissions or selectedSet changes
	useEffect(() => {
		if (organisationId) {
			setSelectedPermissionSet(selectedSet);
			setOrganisationPermissions((prev) => ({
				...prev,
				[organisationId]: {
					permissions,
					permissionSet: selectedSet,
				},
			}));
		}
	}, [
		permissions,
		selectedSet,
		organisationId,
		setOrganisationPermissions,
		setSelectedPermissionSet,
	]);

	const handlePermissionSetChange = (e) => {
		const set = e.target.value;
		setSelectedSet(set);
		setPermissions(normalizePermissions(mapPermissionsFromPreset(set)));
		// Also update the global context so global permissions can react
		setSelectedPermissionSet(set);
	};

	const handleToggleAll = (groupIndex, checked) => {
		setPermissions((prev) =>
			normalizePermissions(
				prev.map((p, idx) =>
					idx === groupIndex
						? {
								...p,
								items: p.items.map((i) => ({ ...i, checked })),
							}
						: p
				)
			)
		);
	};

	const handleToggleItem = (groupIndex, itemKey) => {
		setPermissions((prev) =>
			normalizePermissions(
				prev.map((p, idx) =>
					idx === groupIndex
						? {
								...p,
								items: p.items.map((i) =>
									i.key === itemKey
										? { ...i, checked: !i.checked }
										: i
								),
							}
						: p
				)
			)
		);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const form = e.target.closest("form");
		if (!form?.reportValidity()) {
			return;
		}
		const success = await saveAllPermissions(organisationId, _isNewOrganisation);
		if (success && onSaveSuccess) {
			onSaveSuccess();
		}
	};

	return (
		<CollapsibleSection
			title={organisationName || "Add Organisation"}
			flat={_isNewOrganisation}
			{...(_isNewOrganisation ? { open: true } : {})}
		>
			<form
				onSubmit={handleSubmit}
				ref={contentRef}
				className="space-y-4"
			>
				<input type="hidden" name="user_id" value={userId} />
				{organisationId && (
					<input
						type="hidden"
						name="organisation_id"
						value={organisationId}
					/>
				)}

				<UserPermissionSelect
					value={selectedSet}
					onChange={handlePermissionSetChange}
					permissionSets={permissionSets}
					disabled={disabled}
				/>

				<div className="py-4">
					<label className="block text-sm font-bold">
						Permissions
					</label>
					{permissions.map((group, groupIndex) => (
						<div key={group.key} className="overflow-hidden">
							<div className="flex items-center bg-footer-header p-4">
								<Checkbox
									label={group.label}
									checked={group.items.every(
										(i) => i.checked
									)}
									onChange={(checked) =>
										handleToggleAll(groupIndex, checked)
									}
									controlled={true}
									className="font-bold"
									disabled={user.uwr || disabled}
								/>
							</div>
							<div>
								{group.items.map((item) => {
									const needsMet =
										!item.needs ||
										item.needs.every((need) =>
											hasCheckedPermission(
												permissions,
												need
											)
										);

									return (
										<div
											key={item.key}
											className="bg-background p-4 pl-8 hover:bg-white"
										>
											<div className="flex items-center gap-2">
												<Checkbox
													label={item.label}
													name={item.key}
													checked={item.checked}
													onChange={() =>
														handleToggleItem(
															groupIndex,
															item.key
														)
													}
													controlled={true}
													disabled={
														user.uwr ||
														disabled ||
														!needsMet
													}
												/>
												{permissionSets[
													selectedSet
												]?.preset?.includes(
													item.key
												) && (
													<Badge
														variant="the-brown"
														size="sm"
														rounded
													>
														Default permission
													</Badge>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					))}
				</div>

				{!disabled && (
					<div className="flex gap-2">
						<Button
							type="submit"
							variant="primary"
							disabled={user.uwr}
						>
							Save
						</Button>
						{onDelete && (
							<Button
								type="button"
								onClick={onDelete}
								variant="danger"
							>
								Delete
							</Button>
						)}
					</div>
				)}
			</form>
		</CollapsibleSection>
	);
}

export default UserOrganisationPermissions;
